/**
 * Admin — Archetype Progression Designer & Simulator
 *
 * Two-tab admin page:
 *   1. Designer — view/configure archetypes and their milestone progressions
 *   2. Simulator — pick an archetype, click action buttons to simulate user
 *      behavior, watch features unlock in real-time (no database writes)
 *
 * DEV-505: Archetype Progression Designer
 * DEV-513: Archetype Progression Simulator
 *
 * English only (system admin UI).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchArchetypes,
  fetchMilestones,
  fetchFeatureFlagRegistry,
  simulateProgression,
  updateArchetype,
  saveArchetypeMilestones,
  type ArchetypeResponse,
  type ArchetypeMilestoneMapResponse,
  type MilestoneResponse,
  type FeatureFlagDefinition,
  type SimulationStateResponse,
} from '../../api/adminOnboarding';
import styles from './AdminOnboarding.module.css';
import AdminOnboardingCopy from './AdminOnboardingCopy';

// ── Types ────────────────────────────────────────────────────

type Tab = 'designer' | 'simulator' | 'copy';

// ═══════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════

export default function AdminOnboarding() {
  const [tab, setTab] = useState<Tab>('designer');
  const [archetypes, setArchetypes] = useState<ArchetypeResponse[]>([]);
  const [milestones, setMilestones] = useState<MilestoneResponse[]>([]);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlagDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Load data ──────────────────────────────────────────────

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [a, m, f] = await Promise.all([
        fetchArchetypes(),
        fetchMilestones(),
        fetchFeatureFlagRegistry(),
      ]);
      setArchetypes(a);
      setMilestones(m);
      setFeatureFlags(f);
    } catch (err: any) {
      setError(err.message || 'Failed to load onboarding data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Render ─────────────────────────────────────────────────

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Onboarding System</h1>
        <p className={styles.pageSubtitle}>
          Design archetype progressions and simulate user journeys.
          Changes to milestone mappings affect how new and existing users unlock features.
        </p>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${tab === 'designer' ? styles.tabActive : ''}`}
          onClick={() => setTab('designer')}
        >
          Progression Designer
        </button>
        <button
          className={`${styles.tab} ${tab === 'simulator' ? styles.tabActive : ''}`}
          onClick={() => setTab('simulator')}
        >
          Simulator
        </button>
        <button
          className={`${styles.tab} ${tab === 'copy' ? styles.tabActive : ''}`}
          onClick={() => setTab('copy')}
        >
          Copy
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading onboarding configuration...</div>
      ) : tab === 'designer' ? (
        <DesignerTab
          archetypes={archetypes}
          milestones={milestones}
          featureFlags={featureFlags}
          onReload={loadData}
        />
      ) : tab === 'copy' ? (
        // Skip 2026-07-07 — welcome screen + Getting-started copy editor
        // with the no-signup preview (onboarding_copy, migration 265).
        <AdminOnboardingCopy />
      ) : (
        <SimulatorTab
          archetypes={archetypes}
          milestones={milestones}
          featureFlags={featureFlags}
        />
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// Designer Tab
// ═══════════════════════════════════════════════════════════════

function DesignerTab({
  archetypes,
  milestones,
  featureFlags,
  onReload,
}: {
  archetypes: ArchetypeResponse[];
  milestones: MilestoneResponse[];
  featureFlags: FeatureFlagDefinition[];
  onReload: () => Promise<void>;
}) {
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const selected = archetypes.find(a => a.slug === selectedSlug) ?? null;

  return (
    <>
      {/* Archetype grid */}
      <div className={styles.archetypeGrid}>
        {archetypes.map(a => (
          <button
            key={a.slug}
            className={`${styles.archetypeCard} ${a.slug === selectedSlug ? styles.archetypeCardSelected : ''}`}
            onClick={() => setSelectedSlug(a.slug === selectedSlug ? null : a.slug)}
          >
            <div className={styles.archetypeCardHeader}>
              <span className={styles.archetypeIcon}>{a.icon || '🔲'}</span>
              <div className={styles.archetypeInfo}>
                <div className={styles.archetypeName}>{a.name}</div>
                <div className={styles.archetypeSlug}>{a.slug}</div>
              </div>
            </div>
            <div className={styles.archetypeDesc}>
              {a.description || 'No description'}
            </div>
            <div className={styles.archetypeMeta}>
              <span className={`${styles.archetypeMetaBadge} ${a.isActive ? styles.archetypeMetaActive : styles.archetypeMetaInactive}`}>
                {a.isActive ? 'Active' : 'Inactive'}
              </span>
              <span className={styles.archetypeMetaBadge}>
                {a.milestones.length} milestones
              </span>
              <span className={styles.archetypeMetaBadge}>
                {a.defaultFeatures.length} default features
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Selected archetype detail */}
      {selected && (
        <ArchetypeDetail
          archetype={selected}
          allMilestones={milestones}
          featureFlags={featureFlags}
          onReload={onReload}
        />
      )}
    </>
  );
}


// ═══════════════════════════════════════════════════════════════
// Archetype Detail Panel (with edit mode)
// ═══════════════════════════════════════════════════════════════

/** Local edit state for a milestone mapping row */
interface EditMilestoneRow {
  id?: string;
  milestoneId: string;
  milestoneSlug: string;
  milestoneName: string;
  milestoneTriggerType: string;
  featuresUnlocked: string[];
  isPreCompleted: boolean;
  sortOrder: number;
}

function ArchetypeDetail({
  archetype,
  allMilestones,
  featureFlags,
  onReload,
}: {
  archetype: ArchetypeResponse;
  allMilestones: MilestoneResponse[];
  featureFlags: FeatureFlagDefinition[];
  onReload: () => Promise<void>;
}) {
  // ── Edit state ────────────────────────────────────────────
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  // Editable copies of archetype data
  const [editFeatures, setEditFeatures] = useState<string[]>([]);
  const [editMilestones, setEditMilestones] = useState<EditMilestoneRow[]>([]);
  const [editLandingRoute, setEditLandingRoute] = useState('');
  const [editSelfSelectable, setEditSelfSelectable] = useState(false);

  // Track dirty state
  const [isDirty, setIsDirty] = useState(false);

  // Reset edit state when archetype changes or edit mode enters
  const enterEditMode = useCallback(() => {
    setEditFeatures([...archetype.defaultFeatures]);
    setEditMilestones(archetype.milestones.map(m => ({
      id: m.id,
      milestoneId: m.milestoneId,
      milestoneSlug: m.milestoneSlug,
      milestoneName: m.milestoneName,
      milestoneTriggerType: m.milestoneTriggerType,
      featuresUnlocked: [...m.featuresUnlocked],
      isPreCompleted: m.isPreCompleted,
      sortOrder: m.sortOrder,
    })));
    setEditLandingRoute(archetype.defaultLandingRoute);
    setEditSelfSelectable(archetype.isSelfSelectable);
    setIsDirty(false);
    setSaveMsg(null);
    setEditing(true);
  }, [archetype]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setIsDirty(false);
    setSaveMsg(null);
  }, []);

  // ── Save ──────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      // 1. Save archetype properties (default features, landing route, etc.)
      await updateArchetype(archetype.slug, {
        name: archetype.name,
        description: archetype.description,
        icon: archetype.icon,
        defaultLandingRoute: editLandingRoute,
        defaultFeatures: editFeatures,
        isSelfSelectable: editSelfSelectable,
        minTrustLevelToInvite: archetype.minTrustLevelToInvite,
        isActive: archetype.isActive,
        sortOrder: archetype.sortOrder,
      });

      // 2. Save milestone mappings (bulk replace)
      await saveArchetypeMilestones(archetype.slug, editMilestones.map((m, i) => ({
        id: m.id,
        milestoneId: m.milestoneId,
        featuresUnlocked: m.featuresUnlocked,
        isPreCompleted: m.isPreCompleted,
        sortOrder: i,
      })));

      // 3. Reload data and exit edit mode
      await onReload();
      setEditing(false);
      setIsDirty(false);
      setSaveMsg('Saved successfully');
      setTimeout(() => setSaveMsg(null), 3000);
    } catch (err: any) {
      setSaveMsg(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }, [archetype, editFeatures, editMilestones, editLandingRoute, editSelfSelectable, onReload]);

  // ── Feature toggle ────────────────────────────────────────

  const toggleFeature = useCallback((key: string) => {
    setEditFeatures(prev => {
      const next = prev.includes(key) ? prev.filter(f => f !== key) : [...prev, key];
      setIsDirty(true);
      return next;
    });
  }, []);

  // ── Milestone editing helpers ─────────────────────────────

  const toggleMilestoneFeature = useCallback((milestoneIdx: number, featureKey: string) => {
    setEditMilestones(prev => {
      const next = [...prev];
      const row = { ...next[milestoneIdx] };
      row.featuresUnlocked = row.featuresUnlocked.includes(featureKey)
        ? row.featuresUnlocked.filter(f => f !== featureKey)
        : [...row.featuresUnlocked, featureKey];
      next[milestoneIdx] = row;
      setIsDirty(true);
      return next;
    });
  }, []);

  const toggleMilestonePreCompleted = useCallback((milestoneIdx: number) => {
    setEditMilestones(prev => {
      const next = [...prev];
      next[milestoneIdx] = { ...next[milestoneIdx], isPreCompleted: !next[milestoneIdx].isPreCompleted };
      setIsDirty(true);
      return next;
    });
  }, []);

  const moveMilestone = useCallback((idx: number, direction: -1 | 1) => {
    setEditMilestones(prev => {
      const next = [...prev];
      const target = idx + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      setIsDirty(true);
      return next;
    });
  }, []);

  const removeMilestone = useCallback((idx: number) => {
    setEditMilestones(prev => {
      setIsDirty(true);
      return prev.filter((_, i) => i !== idx);
    });
  }, []);

  const addMilestone = useCallback((milestoneId: string) => {
    const milestone = allMilestones.find(m => m.id === milestoneId);
    if (!milestone) return;
    setEditMilestones(prev => {
      setIsDirty(true);
      return [...prev, {
        milestoneId: milestone.id,
        milestoneSlug: milestone.slug,
        milestoneName: milestone.name,
        milestoneTriggerType: milestone.triggerType,
        featuresUnlocked: [],
        isPreCompleted: false,
        sortOrder: prev.length,
      }];
    });
  }, [allMilestones]);

  // Milestones available to add (not already assigned)
  const availableMilestones = useMemo(() => {
    const assignedIds = new Set(editing ? editMilestones.map(m => m.milestoneId) : archetype.milestones.map(m => m.milestoneId));
    return allMilestones.filter(m => !assignedIds.has(m.id));
  }, [allMilestones, editing, editMilestones, archetype.milestones]);

  // All features this archetype can possibly unlock
  const allPossibleFeatures = useMemo(() => {
    const src = editing ? editFeatures : archetype.defaultFeatures;
    const mils = editing ? editMilestones : archetype.milestones;
    const set = new Set(src);
    for (const m of mils) {
      for (const f of m.featuresUnlocked) set.add(f);
    }
    return set;
  }, [editing, editFeatures, editMilestones, archetype]);

  const neverUnlocked = useMemo(() => {
    return featureFlags.filter(f => !allPossibleFeatures.has(f.key));
  }, [featureFlags, allPossibleFeatures]);

  // ── Render ────────────────────────────────────────────────

  const currentFeatures = editing ? editFeatures : archetype.defaultFeatures;
  const currentMilestones = editing ? editMilestones : archetype.milestones.map(m => ({
    id: m.id,
    milestoneId: m.milestoneId,
    milestoneSlug: m.milestoneSlug,
    milestoneName: m.milestoneName,
    milestoneTriggerType: m.milestoneTriggerType,
    featuresUnlocked: m.featuresUnlocked,
    isPreCompleted: m.isPreCompleted,
    sortOrder: m.sortOrder,
  }));

  return (
    <>
      {/* ── Configuration section ── */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>
            {archetype.icon} {archetype.name} — Configuration
          </h3>
          <div className={styles.editToolbar}>
            {saveMsg && (
              <span className={saveMsg.startsWith('Error') ? styles.error : styles.saveSuccess}>
                {saveMsg}
              </span>
            )}
            {editing ? (
              <>
                {saving && <span className={styles.savingIndicator}>Saving...</span>}
                <button className={styles.btnCancel} onClick={cancelEdit} disabled={saving}>Cancel</button>
                <button
                  className={`${styles.btnSave} ${isDirty ? styles.btnSaveDirty : ''}`}
                  onClick={handleSave}
                  disabled={saving || !isDirty}
                >
                  Save Changes
                </button>
              </>
            ) : (
              <button className={styles.btnEdit} onClick={enterEditMode}>Edit</button>
            )}
          </div>
        </div>

        <div className={styles.detailPanel}>
          <div className={styles.detailField}>
            <span className={styles.detailLabel}>Landing Route</span>
            {editing ? (
              <input
                className={styles.editInput}
                value={editLandingRoute}
                onChange={e => { setEditLandingRoute(e.target.value); setIsDirty(true); }}
              />
            ) : (
              <span className={styles.detailValue}>{archetype.defaultLandingRoute}</span>
            )}
          </div>
          <div className={styles.detailField}>
            <span className={styles.detailLabel}>Self-Selectable</span>
            {editing ? (
              <select
                className={styles.editSelect}
                value={editSelfSelectable ? 'yes' : 'no'}
                onChange={e => { setEditSelfSelectable(e.target.value === 'yes'); setIsDirty(true); }}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            ) : (
              <span className={styles.detailValue}>{archetype.isSelfSelectable ? 'Yes' : 'No'}</span>
            )}
          </div>
          <div className={styles.detailField}>
            <span className={styles.detailLabel}>Min Trust to Invite</span>
            <span className={styles.detailValue}>{archetype.minTrustLevelToInvite || '—'}</span>
          </div>
          <div className={styles.detailField}>
            <span className={styles.detailLabel}>Sort Order</span>
            <span className={styles.detailValue}>{archetype.sortOrder}</span>
          </div>
        </div>

        {/* Default features — toggleable in edit mode */}
        <div style={{ marginTop: 16 }}>
          <span className={styles.detailLabel}>
            Default Features (baseline){editing ? ' — click to toggle' : ''}
          </span>
          {editing ? (
            <div className={styles.featureToggleList}>
              {featureFlags.map(f => {
                const isOn = editFeatures.includes(f.key);
                return (
                  <button
                    key={f.key}
                    className={`${styles.featureToggle} ${isOn ? styles.featureToggleOn : styles.featureToggleOff}`}
                    onClick={() => toggleFeature(f.key)}
                    title={f.description}
                  >
                    {isOn ? '✓ ' : ''}{f.key}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className={styles.featureTagList}>
              {currentFeatures.length === 0 ? (
                <span style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>None</span>
              ) : (
                currentFeatures.map(f => (
                  <span key={f} className={styles.featureTag}>{f}</span>
                ))
              )}
            </div>
          )}
        </div>

        {/* Never-unlocked features */}
        {!editing && neverUnlocked.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <span className={styles.detailLabel}>Never Unlocked by This Archetype</span>
            <div className={styles.featureTagList}>
              {neverUnlocked.map(f => (
                <span key={f.key} className={styles.featureTag} style={{ color: '#9ca3af', background: '#f3f4f6' }}>
                  {f.key}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Milestone progression section ── */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Milestone Progression</h3>
        </div>
        <p className={styles.sectionHint}>
          {editing
            ? 'Reorder milestones, toggle features each unlocks, add/remove milestones, and mark pre-completed.'
            : 'Ordered milestones that unlock additional features as the user progresses.'}
        </p>

        {editing ? (
          /* ── Edit mode: milestone rows ── */
          <>
            {currentMilestones.length === 0 && (
              <div className={styles.emptyMilestones}>
                No milestones assigned. Add one below.
              </div>
            )}
            {currentMilestones.map((m, i) => (
              <div key={`${m.milestoneId}-${i}`} className={styles.milestoneEditRow}>
                <span className={styles.milestoneEditNum}>{i + 1}</span>
                <div className={styles.milestoneEditBody}>
                  <div className={styles.milestoneEditHeader}>
                    <span className={styles.milestoneEditName}>{m.milestoneName}</span>
                    <span className={`${styles.milestoneTrigger} ${triggerClass(m.milestoneTriggerType)}`}>
                      {m.milestoneTriggerType}
                    </span>
                    <span className={styles.milestoneSlug}>{m.milestoneSlug}</span>
                    <label className={styles.preCompletedToggle}>
                      <input
                        type="checkbox"
                        checked={m.isPreCompleted}
                        onChange={() => toggleMilestonePreCompleted(i)}
                      />
                      Pre-completed
                    </label>
                  </div>
                  {/* Features unlocked by this milestone — toggle chips */}
                  <div>
                    <span className={styles.detailLabel} style={{ fontSize: 10, marginBottom: 4, display: 'block' }}>
                      Features unlocked — click to toggle
                    </span>
                    <div className={styles.featureToggleList}>
                      {featureFlags.map(f => {
                        const isOn = m.featuresUnlocked.includes(f.key);
                        return (
                          <button
                            key={f.key}
                            className={`${styles.featureToggle} ${isOn ? styles.featureToggleOn : styles.featureToggleOff}`}
                            onClick={() => toggleMilestoneFeature(i, f.key)}
                            title={f.description}
                            style={{ fontSize: 10, padding: '2px 7px' }}
                          >
                            {isOn ? '✓ ' : ''}{f.key}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className={styles.milestoneEditActions}>
                  <button
                    className={styles.btnMoveUp}
                    onClick={() => moveMilestone(i, -1)}
                    disabled={i === 0}
                    title="Move up"
                  >↑</button>
                  <button
                    className={styles.btnMoveDown}
                    onClick={() => moveMilestone(i, 1)}
                    disabled={i === currentMilestones.length - 1}
                    title="Move down"
                  >↓</button>
                  <button
                    className={styles.btnRemoveMilestone}
                    onClick={() => removeMilestone(i)}
                    title="Remove milestone"
                  >×</button>
                </div>
              </div>
            ))}

            {/* Add milestone */}
            {availableMilestones.length > 0 && (
              <div className={styles.addMilestoneRow}>
                <select
                  className={styles.addMilestoneSelect}
                  id={`add-milestone-${archetype.slug}`}
                  defaultValue=""
                >
                  <option value="" disabled>Add a milestone...</option>
                  {availableMilestones.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.name} ({m.triggerType})
                    </option>
                  ))}
                </select>
                <button
                  className={styles.btnAddMilestone}
                  onClick={() => {
                    const sel = document.getElementById(`add-milestone-${archetype.slug}`) as HTMLSelectElement;
                    if (sel?.value) {
                      addMilestone(sel.value);
                      sel.value = '';
                    }
                  }}
                >
                  + Add
                </button>
              </div>
            )}
          </>
        ) : (
          /* ── Read mode: milestone table ── */
          <>
            {archetype.milestones.length === 0 ? (
              <div className={styles.emptyMilestones}>
                No milestone progression — all features are in default_features (e.g. Advanced, Portfolio Observer)
              </div>
            ) : (
              <table className={styles.milestoneTable}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Milestone</th>
                    <th>Trigger</th>
                    <th>Features Unlocked</th>
                    <th>Pre-completed</th>
                  </tr>
                </thead>
                <tbody>
                  {archetype.milestones.map((m, i) => (
                    <tr key={m.id}>
                      <td>
                        <span className={styles.milestoneOrder}>{i + 1}</span>
                      </td>
                      <td>
                        <div className={styles.milestoneName}>{m.milestoneName}</div>
                        <div className={styles.milestoneSlug}>{m.milestoneSlug}</div>
                      </td>
                      <td>
                        <span className={`${styles.milestoneTrigger} ${triggerClass(m.milestoneTriggerType)}`}>
                          {m.milestoneTriggerType}
                        </span>
                      </td>
                      <td>
                        <div className={styles.featureTagList}>
                          {m.featuresUnlocked.length === 0 ? (
                            <span style={{ fontSize: 11, color: '#d1d5db' }}>—</span>
                          ) : (
                            m.featuresUnlocked.map(f => (
                              <span key={f} className={styles.featureTag}>{f}</span>
                            ))
                          )}
                        </div>
                      </td>
                      <td>
                        {m.isPreCompleted && (
                          <span className={styles.preCompletedBadge}>Pre-completed</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </>
  );
}

function triggerClass(type: string): string {
  switch (type) {
    case 'event': return styles.triggerEvent;
    case 'count': return styles.triggerCount;
    case 'time': return styles.triggerTime;
    case 'compound': return styles.triggerCompound;
    default: return '';
  }
}


// ═══════════════════════════════════════════════════════════════
// Simulator Tab
// ═══════════════════════════════════════════════════════════════

function SimulatorTab({
  archetypes,
  milestones,
  featureFlags,
}: {
  archetypes: ArchetypeResponse[];
  milestones: MilestoneResponse[];
  featureFlags: FeatureFlagDefinition[];
}) {
  const [selectedSlug, setSelectedSlug] = useState(archetypes[0]?.slug ?? '');
  const [completedSlugs, setCompletedSlugs] = useState<Set<string>>(new Set());
  const [simResult, setSimResult] = useState<SimulationStateResponse | null>(null);
  const [prevFeatures, setPrevFeatures] = useState<Set<string>>(new Set());
  const [simLoading, setSimLoading] = useState(false);

  const selectedArchetype = archetypes.find(a => a.slug === selectedSlug);

  // Milestones relevant to the selected archetype (in progression order)
  const archetypeMilestones = useMemo(() => {
    if (!selectedArchetype) return [];
    return selectedArchetype.milestones;
  }, [selectedArchetype]);

  // All milestones that are available as actions (non-pre-completed ones)
  const actionableMilestones = useMemo(() => {
    return archetypeMilestones.filter(m => !m.isPreCompleted);
  }, [archetypeMilestones]);

  // Run simulation whenever archetype or completed milestones change
  const runSimulation = useCallback(async (slug: string, completed: Set<string>) => {
    setSimLoading(true);
    try {
      const result = await simulateProgression(slug, Array.from(completed));
      setSimResult(prev => {
        // Track previously active features for "just unlocked" animation
        if (prev) {
          setPrevFeatures(new Set(prev.activeFeatures));
        }
        return result;
      });
    } catch (err: any) {
      console.error('Simulation failed:', err);
    } finally {
      setSimLoading(false);
    }
  }, []);

  // Initial simulation on archetype change
  useEffect(() => {
    if (selectedSlug) {
      setCompletedSlugs(new Set());
      setPrevFeatures(new Set());
      setSimResult(null);
      runSimulation(selectedSlug, new Set());
    }
  }, [selectedSlug, runSimulation]);

  // Toggle a milestone completion
  const toggleMilestone = useCallback((milestoneSlug: string) => {
    setCompletedSlugs(prev => {
      const next = new Set(prev);
      if (next.has(milestoneSlug)) {
        next.delete(milestoneSlug);
      } else {
        next.add(milestoneSlug);
      }
      runSimulation(selectedSlug, next);
      return next;
    });
  }, [selectedSlug, runSimulation]);

  // Reset simulation
  const resetSimulation = useCallback(() => {
    setCompletedSlugs(new Set());
    setPrevFeatures(new Set());
    runSimulation(selectedSlug, new Set());
  }, [selectedSlug, runSimulation]);

  // Compute progress
  const totalMilestones = archetypeMilestones.length;
  const completedCount = simResult
    ? simResult.milestones.filter(m => m.isCompleted).length
    : 0;
  const progressPct = totalMilestones > 0
    ? Math.round((completedCount / totalMilestones) * 100)
    : 100;

  // Features that were just unlocked (in current result but not in prev)
  const justUnlocked = useMemo(() => {
    if (!simResult) return new Set<string>();
    return new Set(simResult.activeFeatures.filter(f => !prevFeatures.has(f)));
  }, [simResult, prevFeatures]);

  // All feature flags with active status
  const featureMatrix = useMemo(() => {
    const activeSet = new Set(simResult?.activeFeatures ?? []);
    return featureFlags.map(f => ({
      key: f.key,
      description: f.description,
      active: activeSet.has(f.key),
      justUnlocked: justUnlocked.has(f.key),
    }));
  }, [featureFlags, simResult, justUnlocked]);

  // Find next nudge
  const nextNudge = useMemo(() => {
    if (!simResult) return null;
    const nextUncompletedMilestone = simResult.milestones.find(m => !m.isCompleted);
    if (!nextUncompletedMilestone) return null;
    // Look up the milestone in the full milestones list for nudge text
    const fullMilestone = milestones.find(m => m.slug === nextUncompletedMilestone.slug);
    return fullMilestone?.nudgeTextKey ?? null;
  }, [simResult, milestones]);

  if (archetypes.length === 0) {
    return (
      <div className={styles.noData}>
        <div className={styles.noDataIcon}>📋</div>
        No archetypes found. Run migration 088_seed_onboarding.sql first.
        <div className={styles.noDataHint}>
          docker exec -i l4e_postgres psql -U l4e_admin -d life4earth_v2 &lt; migrations/088_seed_onboarding.sql
        </div>
      </div>
    );
  }

  return (
    <div className={styles.simulator}>
      {/* ── Left panel: controls ── */}
      <div className={styles.simControls}>
        <div>
          <span className={styles.detailLabel}>Archetype</span>
          <select
            className={styles.simArchetypeSelect}
            value={selectedSlug}
            onChange={e => setSelectedSlug(e.target.value)}
          >
            {archetypes.map(a => (
              <option key={a.slug} value={a.slug}>
                {a.icon} {a.name}
              </option>
            ))}
          </select>
        </div>

        {/* Progress bar */}
        <div>
          <div className={styles.progressBar}>
            <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
          </div>
          <div className={styles.progressLabel}>
            <span>{completedCount} / {totalMilestones} milestones</span>
            <span>{progressPct}%</span>
          </div>
        </div>

        {/* Action buttons */}
        <div>
          <span className={styles.detailLabel}>Simulate Actions</span>
          <div className={styles.simActionList}>
            {actionableMilestones.length === 0 ? (
              <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic', padding: '8px 0' }}>
                No milestone progression for this archetype
              </div>
            ) : (
              actionableMilestones.map(m => {
                const isCompleted = completedSlugs.has(m.milestoneSlug);
                return (
                  <button
                    key={m.milestoneSlug}
                    className={`${styles.simActionBtn} ${isCompleted ? styles.simActionCompleted : ''}`}
                    onClick={() => toggleMilestone(m.milestoneSlug)}
                    disabled={simLoading}
                  >
                    <span className={styles.simActionIcon}>
                      {isCompleted ? '✓' : '○'}
                    </span>
                    <span className={styles.simActionLabel}>
                      {m.milestoneName}
                    </span>
                    <span className={styles.simActionTrigger}>
                      {m.milestoneTriggerType}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <button
          className={styles.simResetBtn}
          onClick={resetSimulation}
          disabled={completedSlugs.size === 0}
        >
          Reset Simulation
        </button>
      </div>

      {/* ── Right panel: results ── */}
      <div className={styles.simResults}>
        {/* Timeline */}
        {simResult && simResult.milestones.length > 0 && (
          <div className={styles.simResultCard}>
            <h4 className={styles.simResultTitle}>
              Milestone Timeline
            </h4>
            <div className={styles.timeline}>
              {simResult.milestones.map((m, i) => (
                <div key={m.slug} className={styles.timelineItem}>
                  <div
                    className={`${styles.timelineDot} ${
                      m.isCompleted
                        ? (completedSlugs.has(m.slug)
                          ? styles.timelineDotCompleted
                          : styles.timelineDotPreCompleted)
                        : ''
                    }`}
                  />
                  {i < simResult.milestones.length - 1 && (
                    <div className={styles.timelineConnector} />
                  )}
                  <div className={styles.timelineContent}>
                    <div className={`${styles.timelineMilestone} ${m.isCompleted ? styles.timelineMilestoneCompleted : ''}`}>
                      {m.name}
                      {m.isCompleted && !completedSlugs.has(m.slug) && (
                        <span style={{ fontSize: 10, color: '#60a5fa', marginInlineStart: 6 }}>
                          (pre-completed)
                        </span>
                      )}
                    </div>
                    {m.featuresUnlocked.length > 0 && (
                      <div className={styles.timelineFeatures}>
                        {m.featuresUnlocked.map(f => (
                          <span
                            key={f}
                            className={`${styles.featureChip} ${
                              m.isCompleted
                                ? (justUnlocked.has(f) ? styles.featureJustUnlocked : styles.featureActive)
                                : styles.featureLocked
                            }`}
                          >
                            {f}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Feature matrix */}
        <div className={styles.simResultCard}>
          <h4 className={styles.simResultTitle}>
            Feature Flags
            <span className={styles.simResultCount}>
              {featureMatrix.filter(f => f.active).length} / {featureMatrix.length} active
            </span>
          </h4>
          <div className={styles.featureMatrix}>
            {featureMatrix.map(f => (
              <span
                key={f.key}
                className={`${styles.featureChip} ${
                  f.justUnlocked
                    ? styles.featureJustUnlocked
                    : f.active
                    ? styles.featureActive
                    : styles.featureLocked
                }`}
                title={f.description}
              >
                {f.key}
              </span>
            ))}
          </div>
        </div>

        {/* Nudge preview */}
        {nextNudge && (
          <div className={styles.nudgePreview}>
            <div className={styles.nudgeLabel}>Next Nudge (i18n key)</div>
            <div className={styles.nudgeKey}>{nextNudge}</div>
          </div>
        )}

        {/* Completion state */}
        {simResult && simResult.milestones.length > 0 && simResult.milestones.every(m => m.isCompleted) && (
          <div style={{
            padding: '16px 20px',
            background: '#dcfce7',
            border: '1px solid #bbf7d0',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            color: '#166534',
            textAlign: 'center',
          }}>
            All milestones completed — full feature access achieved
          </div>
        )}
      </div>
    </div>
  );
}
